import { NOT_AVAILABLE } from "@/lib/dataLayer";

import { isFullyManaged } from "./planType";

const DESIGN_SLOTS_PATTERN = /\((\d+)\s*design\s*slots?\)/i;
const PURCHASE_FIRED_STORAGE_KEY = "nlcPurchaseEventFired";
const EVENT_ID_PREFIX = "nlc_purchase_";

export function toPlanNameSlug(planName?: null | string): string {
  const slug = (planName ?? "")
    .replace(DESIGN_SLOTS_PATTERN, "")
    .replaceAll(/\bcore\b/gi, "")
    .trim()
    .toLowerCase()
    .replaceAll(/[^\da-z]+/g, "_")
    .replaceAll(/^_+|_+$/g, "");

  return slug || NOT_AVAILABLE;
}

export function resolvePlanCategory(
  origin?: null | string,
  planName?: null | string,
): string {
  if (!planName?.trim()) return NOT_AVAILABLE;

  return isFullyManaged(origin ?? null, planName)
    ? "fully_managed"
    : "self_managed";
}

export function parseDesignSlots(planName?: null | string): null | number {
  const slots = DESIGN_SLOTS_PATTERN.exec(planName ?? "")?.[1];

  return slots ? Number(slots) : null;
}

export function buildProductId(
  planNameSlug: string,
  designSlots: null | number,
): string {
  if (planNameSlug === NOT_AVAILABLE || designSlots === null) {
    return NOT_AVAILABLE;
  }

  return `${planNameSlug}_${designSlots}_slots`;
}

export function buildEventId(transactionId: string): string {
  return `${EVENT_ID_PREFIX}${transactionId}`;
}

export function toIsoCurrency(
  displayCurrency?: null | string,
  fallback: string = NOT_AVAILABLE,
): string {
  const currency = (displayCurrency ?? "").trim();

  if (!currency) return fallback;
  if (currency.includes("US$")) return "USD";

  return (currency.split(" ")[0] ?? "").toUpperCase() || fallback;
}

function toAmount(major?: null | string): null | number {
  const trimmed = major?.trim();

  if (!trimmed) return null;

  const amount = Number(trimmed);

  return Number.isFinite(amount) ? amount : null;
}

export interface PurchaseEventInput {
  displayCurrency?: null | string;
  origin?: null | string;
  planName?: null | string;
  planPriceMajor?: null | string;
  platform: string;
  transactionId: string;
  valueMajor?: null | string;
}

export function buildPurchaseEvent(
  input: PurchaseEventInput,
): Record<string, unknown> {
  const planName = toPlanNameSlug(input.planName);
  const planCategory = resolvePlanCategory(input.origin, input.planName);
  const designSlots = parseDesignSlots(input.planName);
  const productId = buildProductId(planName, designSlots);
  const planPrice = toAmount(input.planPriceMajor) ?? 0;
  const value = toAmount(input.valueMajor) ?? planPrice;
  const designSlotsValue = designSlots ?? NOT_AVAILABLE;

  return {
    currency: toIsoCurrency(input.displayCurrency),
    design_slots: designSlotsValue,
    event: "purchase",
    event_id: buildEventId(input.transactionId),
    items: [
      {
        item_category: planCategory,
        item_id: productId,
        item_name: planName,
        item_variant: `${designSlotsValue}_design_slots`,
        price: planPrice,
        quantity: 1,
      },
    ],
    page_url: "subscription_success",
    plan_category: planCategory,
    plan_name: planName,
    plan_price: planPrice,
    platform: input.platform,
    product_id: productId,
    section: "purchase_confirmation",
    transaction_id: input.transactionId,
    value,
  };
}

function readFiredTransactions(): string[] {
  try {
    const raw = localStorage.getItem(PURCHASE_FIRED_STORAGE_KEY);

    if (!raw) return [];

    const parsed: unknown = JSON.parse(raw);

    return Array.isArray(parsed)
      ? parsed.filter((id) => typeof id === "string")
      : [];
  } catch {
    return [];
  }
}

export function hasFiredPurchase(transactionId: string): boolean {
  return readFiredTransactions().includes(transactionId);
}

export function markPurchaseFired(transactionId: string): void {
  try {
    const fired = readFiredTransactions();

    if (fired.includes(transactionId)) return;

    localStorage.setItem(
      PURCHASE_FIRED_STORAGE_KEY,
      JSON.stringify([...fired.slice(-19), transactionId]),
    );
  } catch {
    return;
  }
}
